import React, {useContext, useState, useRef, useCallback} from 'react';
import Button from 'js/components/Common/Button/Button';
import Form from 'js/components/Common/FormComponents/Form/Form';
import Input from 'js/components/Common/FormComponents/Input/Input';
import TextArea from 'js/components/Common/FormComponents/TextArea/TextArea';
import ReCaptcha from 'js/components/Common/FormComponents/ReCaptcha/ReCaptcha';
import {ToastMessagesContext} from 'js/context/ToastMessagesContext/ToastMessagesContext';
import RequiredMessage from 'js/components/Common/FormComponents/RequiredMessage/RequiredMessage';

import classes from './ContactForm.module.pcss';
import useFetchCallback from 'js/hooks/useFetchCallback/useFetchCallback';
import parse from 'html-react-parser';
import {CommonContext} from 'js/context/CommonContext/CommonContext';
import contactFormFetchData, {servicesFormFetchData} from './fetchData';
import MixPanelWrapper from 'js/servises/MixPanelWrapper';

interface Props {
	type: 'contactForm' | 'servicesForm'
}

const ContactForm = ({type}: Props) => {
	const organisation = 'organisation';
	const name = 'ContactFormName';
	const email = 'ContactFormEmail';
	const subject = 'ContactFormSubject';
	const checkbox = 'ContactFormCheckBox';
	const message = 'ContactFormMessage';
	const recaptcha = 'ReCaptcha';

	const recaptchaRef = useRef(null);
	const {addToastMessage} = useContext(ToastMessagesContext);
	const [formIsValid, setFormIsValid] = useState<boolean>(false);
	const [isSpinner, setIsSpinner] = useState<boolean>(false);
	const {apiPath} = useContext(CommonContext);

	const setFetchData = useCallback((inputData) => {
		if (type === 'contactForm') {
			return `${contactFormFetchData(inputData, name, email, subject, message, recaptcha)}`;
		} else {
			return `${servicesFormFetchData(inputData, organisation, email, subject, message, recaptcha)}`;
		}
	}, [type]);

	const setUpdateData = useCallback((data) => {
		if (type === 'contactForm') {
			return data?.contactForm?.contactFormResult;
		} else {
			return data?.servicesForm?.servicesFormResult;
		}
	}, [type]);

	const sendMixFormEvent = useCallback(async () => (
		await MixPanelWrapper.TrackEvent(
			'Contact Form Submission',
			{
				category:  type === 'servicesForm' ? 'Services page' : 'Contact page',
				section: type === 'servicesForm' ? 'Services Form section' : 'Contact Form section'
			})
	), [type]);


	const submitHandler = useFetchCallback({
		updateData: data => setUpdateData(data),
		query: inputData => setFetchData(inputData),
		onStart: () => setIsSpinner(true),
		onSuccess: async () => {
			recaptchaRef.current.reset();
			setFormIsValid(false);
			setIsSpinner(false);
			addToastMessage({
				type: 'success',
				key: 'contactFormToasts'
			});
			await sendMixFormEvent();
		},
		onFailure: () => {
			recaptchaRef.current.reset();
			setFormIsValid(false);
			setIsSpinner(false);
			addToastMessage({
				type: 'error',
				key: 'contactFormToasts'
			});
		},
		onError: () => {
			setIsSpinner(false);
			addToastMessage({
				type: 'error',
				key: 'contactFormToasts'
			});
		}
	});

	return (
		<div className={classes.ContactForm}>
			<Form onSubmit={submitHandler} resetAfterSubmit={true} onValidForm={setFormIsValid}>
				<RequiredMessage text={'*All fields are required'} />
				{type === 'contactForm' ?
					<Input
						key={name}
						type={'text'}
						name={name}
						id={name}
						maxLength={25}
						autoComplete={'name'}
						withMargin={true}
						floatingLabel={'Name'}
						validation={{
							rules: [
								{ type: 'required', message: 'Required Field' },
								{ type: 'name', message: 'Invalid Name' }
							]
						}}
					/> : ''
				}
				{type === 'servicesForm' ? <Input
					key={organisation}
					type={'text'}
					name={organisation}
					id={organisation}
					maxLength={25}
					withMargin={true}
					floatingLabel={'Organisation'}
					validation={{
						rules: [
							{ type: 'required', message: 'Required Field' }
						]
					}}
				/> : ''}
				<Input
					key={email}
					type={'text'}
					name={email}
					id={email}
					maxLength={50}
					autoComplete={'email'}
					withMargin={true}
					floatingLabel={'Email'}
					validation={{
						rules: [
							{ type: 'required', message: 'Required Field' },
							{ type: 'email', message: 'Invalid Email' }
						]
					}}
				/>
				<Input
					key={subject}
					type={'text'}
					name={subject}
					id={subject}
					maxLength={50}
					autoComplete={'off'}
					floatingLabel={'Subject'}
					withMargin={true}
					validation={{
						rules: [
							{ type: 'required', message: 'Required Field' }
						]
					}}
				/>

				<TextArea
					type={'text'}
					key={message}
					id={message}
					withMargin={true}
					floatingLabel={'Message us here...'}
					height={160}
					name={message}
					maxLength={10000}
					validation={{
						rules: [
							{ type: 'required', message: 'Required Field' }
						]
					}}
				/>

				<ReCaptcha
					type={recaptcha}
					recaptchaRef={recaptchaRef}
					name={recaptcha}
					size={'normal'}
					validation={{
						rules: [
							{ type: 'required', message: 'Required Field' }
						]
					}}/>

				<Input
					type="checkbox"
					withLabel={true}
					name={checkbox}
					slug={'CheckBoxTermsAndPolicy'}
					labelText={
						parse(
							`<span style="font-size: 12px">I agree to <a target="_blank" rel="nofollow" href="${apiPath}/ol-user-agreement">Terms</a> and the <a target="_blank" rel="nofollow" href="${apiPath}/privacy-policy">Privacy Policy</a></span>`
						)
					}
					defaultValue={true}
					id={'CheckBoxTermsAndPolicy'}
					validation={{
						rules: [
							{ type: 'required', message: 'Required Field' }
						]
					}}
				/>

				<div className={classes.ButtonWrap}>
					<Button withSpinner={isSpinner} text={'Send'} type={'button'} disabled={!formIsValid} />
				</div>
			</Form>
		</div>
	);
};

export default ContactForm;

